/**
 * dataServiceFactory — selects the right adapter pair for a data
 * source key. Returns:
 *   - catalogue: IFixtureCatalogue (built once per source)
 *   - createContext(fixtureId): IDataService factory (called per
 *     fixture selection so the hook can drop the old adapter on change)
 *
 * FIFA source is now end-to-end Apollo: catalogue via
 * allUpcomingFixtures, context via fixtureMetricChannel. PLS remains
 * DDS-backed. The IDataService contract stays identical regardless of
 * which transport is behind it.
 *
 * SOLID:
 *   - S: this module's only job is the source-key switch.
 *   - O: adding a new source = one new case + new adapters. No
 *        existing branch changes.
 *   - L: any returned catalogue conforms to IFixtureCatalogue;
 *        any createContext result conforms to IDataService.
 *   - D: the factory takes transport clients as injections —
 *        SceneControls never imports an adapter directly.
 */
import { createAetherFifaCatalogue } from "./adapters/AetherFifaCatalogue";
import { createAetherFifaContext } from "./adapters/AetherFifaContext";
import { createDdsPlsCatalogue } from "./adapters/DdsPlsCatalogue";
import { createDdsPlsContext } from "./adapters/DdsPlsContext";
import { withContextShape } from "./withContextShape";
import { fifaToDdsShape } from "./fifaToDdsShape";

export function createDataServices({
  source,
  apolloClient,
  ddsClient,
  fifaConfig,
  plsConfig,
}) {
  if (source === "fifa") {
    if (!apolloClient) throw new Error("dataServiceFactory: `apolloClient` is required for FIFA source");
    return {
      catalogue: createAetherFifaCatalogue({
        client: apolloClient,
        competitionId: fifaConfig?.competitionId,
        databaseName: fifaConfig?.databaseName,
      }),
      // FIFA context is reshaped into the DDS-PLS shape so caption
      // definitions (which bind against DDS paths like
      // fixture[0].fixture_id) resolve identically regardless of
      // source. AetherFifaContext stays pure transport; withContextShape
      // + fifaToDdsShape do the translation.
      createContext: (fixtureId) =>
        withContextShape(
          createAetherFifaContext({
            client: apolloClient,
            fixtureId,
            competitionId: fifaConfig?.competitionId,
            databaseName: fifaConfig?.databaseName,
          }),
          (raw) => fifaToDdsShape(raw, { fixtureId }),
        ),
    };
  }

  if (source === "pls") {
    if (!ddsClient) throw new Error("dataServiceFactory: `ddsClient` is required for PLS source");
    return {
      catalogue: createDdsPlsCatalogue({
        client: ddsClient,
        competitionId: plsConfig?.competitionId,
        seasonId: plsConfig?.seasonId,
      }),
      createContext: (fixtureId) =>
        createDdsPlsContext({
          client: ddsClient,
          fixtureId,
          competitionId: plsConfig?.competitionId,
          seasonId: plsConfig?.seasonId,
        }),
    };
  }

  throw new Error(`dataServiceFactory: unknown source "${source}"`);
}
